import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/features/auth';
import { LanguageProvider } from '@/i18n/LanguageContext';
import Banking from '@/pages/Banking';
import Overview from '@/pages/Overview';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from 'next-themes';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { MainLayout } from './components/layout/MainLayout';

const Login = lazy(() => import('@/pages/Login'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const Trade = lazy(() => import('@/pages/Trade'));
const InstrumentDetail = lazy(() => import('@/pages/InstrumentDetail'));
const BotDetail = lazy(() => import('@/pages/BotDetail'));
const PositionDetail = lazy(() => import('@/pages/PositionDetail'));
const OrderDetail = lazy(() => import('@/pages/OrderDetail'));
const NewOrderPage = lazy(() => import('@/pages/NewOrderPage'));
const NewBotPage = lazy(() => import('@/pages/NewBotPage'));
const NewBankingAccountPage = lazy(() => import('@/pages/NewBankingAccountPage'));
const NewTradingAccountPage = lazy(() => import('@/pages/NewTradingAccountPage'));
/** Review + import a bank linked through Plaid — see docs/plaid.md. */
const PlaidLinkedItemPage = lazy(() => import('@/pages/PlaidLinkedItemPage'));
const NewCashFlowMovementPage = lazy(() => import('@/pages/NewCashFlowMovementPage'));
const AccountDetails = lazy(() =>
  import('@/components/accounts/AccountDetails').then((m) => ({ default: m.AccountDetails }))
);
const NewGoalPage = lazy(() => import('@/pages/NewGoalPage'));
const Planning = lazy(() => import('@/pages/Planning'));
const GoalDetails = lazy(() =>
  import('@/components/goals/GoalDetails').then((m) => ({ default: m.GoalDetails }))
);
const Vault = lazy(() => import('@/pages/Vault'));
const DocumentDetails = lazy(() =>
  import('@/components/vault/DocumentDetails.tsx').then((m) => ({ default: m.DocumentDetails }))
);
const Transactions = lazy(() => import('@/pages/Transactions'));
const NewTransactionPage = lazy(() => import('@/pages/NewTransactionPage'));
const TransactionDetails = lazy(() =>
  import('@/components/transactions/TransactionDetails').then((m) => ({
    default: m.TransactionDetails,
  }))
);
const Notifications = lazy(() => import('@/pages/Notifications'));
const SearchResults = lazy(() => import('@/pages/SearchResults'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const OpenSourceLicensesPage = lazy(() => import('@/pages/OpenSourceLicensesPage'));
const PreferencesPage = lazy(() => import('@/pages/PreferencesPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const SecurityPage = lazy(() => import('@/pages/SecurityPage'));
const SessionsPage = lazy(() => import('@/pages/SessionsPage'));
const Settings = lazy(() => import('@/pages/Settings'));
const SettingsConnectionsPage = lazy(() => import('@/pages/SettingsConnectionsPage'));
const ConnectIntegrationPage = lazy(() => import('@/pages/ConnectIntegrationPage'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const PersonalSettingsPage = lazy(() => import('./pages/PersonalSettingsPage'));
const OwnershipStructureSettingsPage = lazy(
  () => import('./pages/settings/OwnershipStructureSettingsPage')
);
const PartiesSettingsPage = lazy(() => import('./pages/settings/PartiesSettingsPage'));
const NewPartyPage = lazy(() => import('@/pages/NewPartyPage'));
const PartyDetailsPage = lazy(() => import('@/pages/PartyDetailsPage'));
const TaxSettingsPage = lazy(() => import('./pages/settings/TaxSettingsPage'));
const SettingsMcpPage = lazy(() => import('@/pages/SettingsMcpPage'));
const AdvisorsSettingsPage = lazy(() => import('./pages/settings/AdvisorsSettingsPage'));
const AdvisorInviteAcceptPage = lazy(() => import('./pages/settings/AdvisorInviteAcceptPage'));
const OAuthConsentPage = lazy(() => import('@/pages/OAuthConsentPage'));

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

function RouteFallback() {
  return null;
}

const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider attribute='class' defaultTheme='system' enableSystem>
        <LanguageProvider>
          <TooltipProvider>
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <Router>
                <AuthProvider>
                  <Toaster />
                  <Sonner />
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      <Route path='/login' element={<Login />} />
                      <Route path='/privacy-policy' element={<PrivacyPolicy />} />
                      <Route path='/terms-of-service' element={<TermsOfService />} />
                      <Route
                        path='/oauth/consent'
                        element={
                          <ProtectedRoute>
                            <OAuthConsentPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path='/'
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <Outlet />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<Navigate to='/overview' replace />} />
                        <Route path='overview' element={<Overview />} />
                        <Route path='overview/:tab' element={<Overview />} />
                        <Route path='trade/instruments/:assetId' element={<InstrumentDetail />} />
                        <Route path='trade/bots/new' element={<NewBotPage />} />
                        <Route path='trade/accounts/new' element={<NewTradingAccountPage />} />
                        <Route path='trade/bots/:botId' element={<BotDetail />} />
                        <Route path='trade/positions/:holdingId' element={<PositionDetail />} />
                        <Route path='trade/orders/:orderId' element={<OrderDetail />} />
                        <Route path='trade/ticket' element={<NewOrderPage />} />
                        <Route path='trade/:tab' element={<Trade />} />
                        <Route path='trade' element={<Navigate to='/trade/positions' replace />} />
                        <Route
                          path='banking'
                          element={<Navigate to='/banking/accounts' replace />}
                        />
                        <Route path='banking/accounts/new' element={<NewBankingAccountPage />} />
                        <Route path='banking/accounts/plaid' element={<PlaidLinkedItemPage />} />
                        <Route path='banking/accounts' element={<Banking />} />
                        <Route
                          path='banking/net-worth'
                          element={<Navigate to='/overview/net-worth' replace />}
                        />
                        <Route path='banking/transactions' element={<Transactions />} />
                        <Route path='banking/transactions/new' element={<NewTransactionPage />} />
                        <Route
                          path='banking/transactions/:transactionId'
                          element={<TransactionDetails />}
                        />
                        <Route
                          path='accounts'
                          element={<Navigate to='/banking/accounts' replace />}
                        />
                        <Route
                          path='accounts/:accountId/transactions/new'
                          element={<NewTransactionPage />}
                        />
                        <Route path='accounts/:accountId/:tab?' element={<AccountDetails />} />
                        <Route path='planning/goals' element={<Planning />} />
                        <Route path='planning/goals/new' element={<NewGoalPage />} />
                        <Route path='planning/goals/:goalId' element={<GoalDetails />} />
                        <Route
                          path='planning/:planId/cash-flow/new'
                          element={<NewCashFlowMovementPage />}
                        />
                        <Route path='planning/:planId/:tab' element={<Planning />} />
                        <Route path='planning/:planId' element={<Planning />} />
                        <Route path='planning' element={<Planning />} />
                        <Route path='vault' element={<Vault />} />
                        <Route path='vault/:documentId' element={<DocumentDetails />} />
                        <Route path='notifications' element={<Notifications />} />
                        <Route path='search' element={<SearchResults />} />
                        <Route path='settings' element={<Settings />} />
                        <Route path='settings/connections' element={<SettingsConnectionsPage />} />
                        <Route
                          path='settings/connections/:provider'
                          element={<ConnectIntegrationPage />}
                        />
                        <Route path='settings/parties' element={<PartiesSettingsPage />} />
                        <Route path='settings/parties/new' element={<NewPartyPage />} />
                        <Route path='settings/parties/:partyId' element={<PartyDetailsPage />} />
                        <Route
                          path='settings/ownership-structure'
                          element={<OwnershipStructureSettingsPage />}
                        />
                        <Route
                          path='settings/estate-structure'
                          element={<Navigate to='/settings/ownership-structure' replace />}
                        />
                        <Route path='settings/tax' element={<TaxSettingsPage />} />
                        <Route path='settings/personal' element={<PersonalSettingsPage />} />
                        <Route path='settings/preferences' element={<PreferencesPage />} />
                        <Route path='settings/notifications' element={<NotificationsPage />} />
                        <Route path='settings/sessions' element={<SessionsPage />} />
                        <Route path='settings/security' element={<SecurityPage />} />
                        <Route path='settings/privacy' element={<PrivacyPage />} />
                        <Route path='settings/mcp' element={<SettingsMcpPage />} />
                        <Route path='settings/advisors' element={<AdvisorsSettingsPage />} />
                        <Route
                          path='settings/advisors/accept'
                          element={<AdvisorInviteAcceptPage />}
                        />
                        <Route path='settings/open-source' element={<OpenSourceLicensesPage />} />
                        <Route path='*' element={<NotFound />} />
                      </Route>
                    </Routes>
                  </Suspense>
                </AuthProvider>
              </Router>
            </GoogleOAuthProvider>
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
